import { createFileRoute } from '@tanstack/react-router';
import { SettingsRow, SettingsSection, Switch, ViewTab, ViewTabList } from '@wundo/ui';
import { SaveStatus } from '@/components/SaveStatus.tsx';
import { useProfileSave } from '@/lib/profileSave.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings/agenda')({
  component: AgendaSettingsPage,
});

/*
 * The values are the ones the column stores, so nothing has to translate
 * between "calendar" here and "week" in the agenda's own toolbar — they were
 * always the same view under two names.
 */
const VIEWS = [
  { value: 'week', label: m.settings_view_calendar() },
  { value: 'list', label: m.settings_view_list() },
] as const;

const WEEK_STARTS = [
  { value: 'monday', label: m.settings_week_monday() },
  { value: 'sunday', label: m.settings_week_sunday() },
] as const;

export function AgendaSettingsPage() {
  const { user, save, pending, failed } = useProfileSave();

  if (!user) return null;

  return (
    <>
      <SaveStatus pending={pending} failed={failed} />
      <SettingsSection title={m.settings_tab_agenda()}>
        <SettingsRow
          label={m.settings_default_view()}
          description={m.settings_default_view_desc()}
        >
          <ViewTabList aria-label={m.settings_default_view()}>
            {VIEWS.map((option) => (
              <ViewTab
                key={option.value}
                active={option.value === user.agenda_default_view}
                onClick={() => save({ agenda_default_view: option.value })}
              >
                {option.label}
              </ViewTab>
            ))}
          </ViewTabList>
        </SettingsRow>
        <SettingsRow
          label={m.settings_week_start()}
          description={m.settings_week_start_desc()}
        >
          <ViewTabList aria-label={m.settings_week_start()}>
            {WEEK_STARTS.map((option) => (
              <ViewTab
                key={option.value}
                active={option.value === user.agenda_week_starts_on}
                onClick={() => save({ agenda_week_starts_on: option.value })}
              >
                {option.label}
              </ViewTab>
            ))}
          </ViewTabList>
        </SettingsRow>
        {/*
         * A Sunday week shows Sunday, so the server turns this back on and the
         * switch follows the saved record rather than the click. Moving the week
         * back to Monday is what frees it again.
         */}
        <SettingsRow label={m.settings_weekend()} description={m.settings_weekend_desc()}>
          <Switch
            aria-label={m.settings_weekend()}
            checked={user.agenda_show_weekend}
            onCheckedChange={(checked) => save({ agenda_show_weekend: checked })}
          />
        </SettingsRow>
      </SettingsSection>
    </>
  );
}
